import React, { Component } from "react";
import Dropzone from "react-dropzone";
import NumberFormat from "react-number-format";
import axios from "axios";
import { isEqual, omit } from "lodash";

// Components
import ImageToolbar from "../utils/image-toolbar/ImageToolbar";
import UploadProgress from "./UploadProgress";
import Modal from "../modal/Modal";
import Button from "../modal/Button";

// Util
import getFileSize from "../../utils/fileSize";

class DropzoneWrapper extends Component {
	constructor(props) {
		super(props);
		this.state = {
			showError: false,
			files: [],
			existingFiles: !!props.initialImages ? props.initialImages : [],
			allowMultiple: props.multiple !== undefined && props.multiple,
			droppedImage: false // Flag to notify component something was dropped
		};
	}

	componentDidMount() {
		this.getFileInfo();
	}

	componentWillUnmount() {
		this.clearPreviewObject();
	}

	componentWillReceiveProps(nextProps) {
		// If the files are set to be cleared
		if (nextProps.clear && this.props.clear !== nextProps.clear) {
			this.setState({
				files: [],
				droppedImage: false
			});

			// Clear the react-drop memory for the preview image
			this.clearPreviewObject();
		}

		// Update files (maybe loading now or something)
		if (nextProps.img.length) {
			this.setState({
				files: nextProps.img
			});
		}

		if (!nextProps.initialImages || (!this.state.multiple && this.state.droppedImage)) return;

		// Compare the array collection of initial images.
		// It's an array of objects, so a simple isEqual wouldn't work
		let equal = true;
		for (let i = 0; i < nextProps.initialImages.length; i++) {
			// Item is not set on the other array
			if (this.state.existingFiles[i] === undefined) {
				equal = false;
				break;
			}

			// Ensure both objects are equal (we don't care about size and date_edited)
			if (
				!isEqual(
					omit(this.state.existingFiles[i], ["size", "date_edited"]),
					omit(nextProps.initialImages[i], ["size", "date_edited"])
				)
			) {
				equal = false;
				break;
			}
		}

		// Update existsing images if there is any new info
		if (!equal) {
			this.setState(
				{
					existingFiles: !!nextProps.initialImages ? nextProps.initialImages : []
				},
				() => this.getFileInfo()
			);
		}
	}

	getFileInfo() {
		if (!this.state.existingFiles.length) return;

		this.state.existingFiles.map((file, i) =>
			axios
				.head(file[this.props.imageProperty])
				.then(resp => {
					const newFile = {
						...file,
						size: resp.headers["content-length"]
					};

					let newFiles = this.state.existingFiles.slice();
					newFiles[i] = newFile;

					this.setState({
						existingFiles: newFiles
					});
				})
				.catch(err => {
					console.error("File info error: ", err);

					// Immutably clone specific variant item
					file = Object.assign({}, this.state.existingFiles[i]);

					// Set new value to item
					file.size = 0;

					// Set the variant to the cloned item
					let existingFiles = this.state.existingFiles;
					existingFiles[i] = file;

					this.setState({
						existingFiles
					});
				})
		);
	}

	clearPreviewObject = () => {
		if (!this.state.files.length) return;
		this.state.files.forEach(file => {
			window.URL.revokeObjectURL(file.preview);
		});
	};

	onDrop(acceptedFiles, rejectedFiles) {
		const files = this.state.allowMultiple ? acceptedFiles : [acceptedFiles[0]];

		files.forEach(file => {
			this.readFile(file);
		});
	}

	readFile(file) {
		const reader = new FileReader();
		reader.onload = () => {
			const fileAsBinaryString = reader.result;
			file.fileString = fileAsBinaryString;

			file.alt = "";
			file.link = "";

			let existingFiles = [];
			let files = [file];

			if (!this.state.allowMultiple) {
				this.clearPreviewObject();

				if (!!this.props.handleExistingImageRemoval) {
					this.props.handleExistingImageRemoval(0);
				}
			} else {
				existingFiles = [...this.state.existingFiles];
				files = [...this.state.files, file];
			}

			this.setState({
				existingFiles,
				files,
				droppedImage: true
			});
			this.props.handleImgUpdate(this.props.name, file);
		};
		reader.onabort = () => console.log("file reading was aborted");
		reader.onerror = () => console.log("file reading has failed");

		// ===================================
		//  Reading will fail if it's not an image
		// ===================================
		try {
			reader.readAsBinaryString(file);
		} catch (err) {
			this.setState({ showError: true });
			console.log(err);
		}
	}

	removeImage(e, index) {
		e.stopPropagation();
		let { files } = this.state;
		files.splice(index, 1);
		this.setState({
			files: files
		});
		this.props.handleImgRemoval(files);
	}

	removeExistingImage(e, index) {
		e.stopPropagation();
		let { existingFiles } = this.state;
		existingFiles.splice(index, 1);
		this.setState({
			existingFiles: existingFiles
		});

		// Call parent handler so it knows an image has been removed
		this.props.handleExistingImageRemoval(index);
	}

	formatName(name) {
		return name.substr(name.lastIndexOf("/") + 1);
	}

	render() {
		const { files, existingFiles } = this.state;
		const { imageProperty, toolbarTypes } = this.props;

		// Dropped images
		const dropped = files.map((file, i) => {
			return (
				<div key={"dropped-" + i} className="multiple-drop-image new-img">
					<img src={file.preview} alt={file.preview} />
					<div className="image-info">
						<div className="image-name">{file.name}</div>
						<NumberFormat
							value={getFileSize(file.size).value}
							displayType={"text"}
							decimalScale={2}
							fixedDecimalScale={true}
							thousandSeparator={true}
							suffix={getFileSize(file.size).suffix}
						/>
					</div>
					<div className="icon-cross" onClick={e => this.removeImage(e, i)} />
					{!!toolbarTypes && <ImageToolbar file={file} index={i} {...this.props} />}
					{file.loading && (
						<UploadProgress completed={file.completed} message={file.message} />
					)}
				</div>
			);
		});

		// Existing images
		const existing = existingFiles.map((file, i) => {
			return (
				<div key={"existing-" + i} className="multiple-drop-image">
					<img src={file[imageProperty]} alt={file[imageProperty]} />
					<div className="image-info">
						<div className="image-name">{this.formatName(file[imageProperty])}</div>
						<NumberFormat
							value={getFileSize(file.size).value}
							displayType={"text"}
							decimalScale={2}
							fixedDecimalScale={true}
							thousandSeparator={true}
							suffix={getFileSize(file.size).suffix}
						/>
					</div>
					<div className="icon-cross" onClick={e => this.removeExistingImage(e, i)} />
					{!!toolbarTypes && <ImageToolbar file={file} index={i} {...this.props} />}
				</div>
			);
		});

		// Combine both types of images
		const images = dropped.concat(existing);

		// console.log('render state', this.state)

		return (
			<React.Fragment>
				<Dropzone
					onDrop={this.onDrop.bind(this)}
					accept="image/*"
					multiple={!!this.props.multiple ? this.props.multiple : false}
					className="dropzone"
					style={{ border: "none" }}
				>
					{/* There are images available */}
					{files.length || existingFiles.length ? (
						images
					) : (
							// There are no images yet
							<div className="txt">
								{this.props.placeholder
									? this.props.placeholder
									: `Drag image here or click to select image`}
							</div>
						)}
				</Dropzone>

				{this.state.showError && (
					<Modal
						title={"Error"}
						buttons={[
							{
								type: Button,
								text: "Close",
								action: () => {
									this.setState({ showError: false });
								}
							}
						]}
					>
						Unable to read file. Please try using another image.
					</Modal>
				)}
			</React.Fragment>
		);
	}
}

export default DropzoneWrapper;



// WEBPACK FOOTER //
// ./src/components/utils/DropzoneWrapper.js